import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Tag } from "lucide-react";

const blank = {
  code: "", discount_type: "fixed", value: "20", max_uses: "1",
  expires_in_days: "30", assigned_to_email: "", note: "", applicable_package_ids: [],
};

export default function Offers() {
  const [coupons, setCoupons] = useState([]);
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get("/coupons");
    setCoupons(data);
  };
  useEffect(() => {
    load();
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const togglePkg = (id) => setForm((f) => ({
    ...f,
    applicable_package_ids: f.applicable_package_ids.includes(id)
      ? f.applicable_package_ids.filter((x) => x !== id)
      : [...f.applicable_package_ids, id],
  }));

  const pkgName = (id) => packages.find((p) => p.id === id)?.name || id;

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        value: parseFloat(form.value),
        max_uses: parseInt(form.max_uses, 10),
        expires_in_days: parseInt(form.expires_in_days, 10),
        assigned_to_email: form.assigned_to_email || null,
        note: form.note || null,
        code: form.code ? form.code.toUpperCase() : null,
        applicable_package_ids: form.applicable_package_ids,
      };
      await api.post("/coupons", payload);
      toast.success("Offer created");
      setForm(blank); setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await api.delete(`/coupons/${id}`);
    load();
  };

  return (
    <div className="p-8 md:p-12" data-testid="offers-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Custom Offers</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Coupons.</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">Create discount codes for any client. Leave the email blank for a public code, or assign one to a specific client’s email for a targeted offer.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="add-coupon-btn">
              <Plus className="h-4 w-4 mr-2" /> New offer
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-lg" data-testid="add-coupon-dialog">
            <DialogHeader><DialogTitle className="font-display uppercase">Create a coupon</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Code (optional)</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="Auto-generated" maxLength={20} className="bg-[#050505] border-[#27272A] rounded-sm uppercase" data-testid="coupon-code-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Type</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="coupon-type-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                      <SelectItem value="fixed">Fixed $ off</SelectItem>
                      <SelectItem value="percent">% off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">{form.discount_type === "percent" ? "% Value" : "$ Value"}</Label>
                  <Input required type="number" min="0.01" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="coupon-value-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Max uses</Label>
                  <Input required type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="coupon-uses-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Expires (days)</Label>
                  <Input required type="number" min="1" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="coupon-expires-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Assigned to client email (optional)</Label>
                <Input type="email" value={form.assigned_to_email} onChange={(e) => setForm({ ...form, assigned_to_email: e.target.value })} placeholder="client@example.com" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="coupon-assigned-input" />
                <p className="text-[11px] text-zinc-500">Leave blank for a public code. If set, only this email can redeem.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Internal note</Label>
                <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="e.g. 'Welcome back offer for John'" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="coupon-note-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Applies to</Label>
                <div className="flex flex-wrap gap-2" data-testid="coupon-packages">
                  {packages.map((p) => {
                    const active = form.applicable_package_ids.includes(p.id);
                    return (
                      <button
                        type="button" key={p.id} onClick={() => togglePkg(p.id)}
                        data-testid={`coupon-pkg-${p.id}`}
                        className={`text-left px-3 py-2 rounded-sm border text-xs transition-colors ${active ? "border-[#FF331F] bg-[#FF331F]/10 text-white" : "border-[#27272A] bg-[#050505] text-zinc-400 hover:border-zinc-600"}`}
                      >
                        <span className="font-bold">{p.name}</span>
                        <span className="text-zinc-500"> · ${Number(p.amount).toFixed(0)}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-zinc-500">Select the offering(s) this code works on. Leave all unselected to make it valid for every offering.</p>
              </div>
              <Button type="submit" className="w-full bg-[#FF331F] hover:bg-[#FF5242] font-bold uppercase tracking-widest rounded-sm" data-testid="save-coupon-btn">Create Offer</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-[#27272A] bg-[#0F0F0F]">
        <table className="w-full text-sm" data-testid="coupons-table">
          <thead className="border-b border-[#27272A] text-zinc-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="text-left p-4">Code</th>
              <th className="text-left p-4">Discount</th>
              <th className="text-left p-4">Applies to</th>
              <th className="text-left p-4">Assigned</th>
              <th className="text-left p-4">Uses</th>
              <th className="text-left p-4">Expires</th>
              <th className="p-4 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 ? (
              <tr><td colSpan={7} className="p-12 text-center text-zinc-500">No offers yet. Create one to send to a client.</td></tr>
            ) : coupons.map((c) => (
              <tr key={c.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`coupon-row-${c.id}`}>
                <td className="p-4">
                  <button onClick={() => copyCode(c.code)} className="font-display uppercase tracking-widest text-[#FF331F] flex items-center gap-2 hover:text-[#FF5242]" data-testid={`copy-coupon-${c.id}`}>
                    <Tag className="h-3.5 w-3.5" /> {c.code} <Copy className="h-3 w-3 opacity-60" />
                  </button>
                  {c.note && <div className="text-[11px] text-zinc-500 mt-1">{c.note}</div>}
                </td>
                <td className="p-4 font-display text-white">
                  {c.discount_type === "percent" ? `${c.value}%` : `$${c.value}`}
                </td>
                <td className="p-4 text-zinc-400 text-xs max-w-[220px]" data-testid={`coupon-applies-${c.id}`}>
                  {c.applicable_package_ids?.length
                    ? c.applicable_package_ids.map(pkgName).join(", ")
                    : <span className="text-zinc-500">All offerings</span>}
                </td>
                <td className="p-4 text-zinc-400 text-xs">{c.assigned_to_email || <span className="text-zinc-600">Public</span>}</td>
                <td className="p-4 text-zinc-400">{c.used_count || 0} / {c.max_uses}</td>
                <td className="p-4 text-zinc-500 text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "Never"}</td>
                <td className="p-4 text-right">
                  <button onClick={() => remove(c.id)} className="text-zinc-500 hover:text-red-400" data-testid={`delete-coupon-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
